import Container from '@material-ui/core/Container'
import React, { FC } from 'react'
import { BlogPostContainer } from './style'

//* utils
import { EntryLocaleMatch, LocaleCodes } from '../../../../constants/localization.constants'
import { ComponentAnnouncement } from '../../../../models/components/announcement'
import { ComponentMainNavBanner } from '../../../../models/components/mainNavBanner'
import { MenuGroup } from '../../../../models/others/menuGroup'

//* components
import { UseCaseTypes } from '../../../../constants/blogUseCase'
import RelatedArticles from '../../../global/relatedArticles'
import SEOTags from '../../../global/seoTags'
import Subscribe from '../../../global/subscribe'
import SubscribeModal from '../../../global/subscribeModal'
import Layout from '../../../layouts/layout'
import BlogNavigation from '../navigation'
import { PostContent } from './subcomponent/postContent'
import { TopSection } from './subcomponent/topSection'

type Props = {
  path: string
  pageContext: any
  headerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  footerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  announcementData?: ComponentAnnouncement
  mainNavBannerData?: ComponentMainNavBanner
  browserLanguageData?: any
}

const BlogPost: FC<Props> = ({
  path,
  pageContext,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}) => {
  if (!pageContext.blogPostData) return null

  const blogData = {
    isSecondaryBlog: pageContext.isSecondaryBlog || false,
    relatedArticles: pageContext.relatedArticles || [],
    blogPostPath: pageContext.pageData?.path || path,
    blogType: pageContext.blogPostData.blogType || '',
    seo: pageContext.blogPostData.seoMetadata || pageContext.blogPostData.seo || null,
    node_locale:
      pageContext.blogPostData.node_locale ||
      EntryLocaleMatch[pageContext.blogPostData.entryLocale] ||
      LocaleCodes.ENGLISH.code,
    slug: pageContext.blogPostData.slug || '',
    name: pageContext.blogPostData.h1Title || '',
    pageName: pageContext.blogPostData.pageName || '',
    body: pageContext.blogPostData.body || '',
    mainImage: {
      url: pageContext.blogPostData.mainImage?.image?.file?.url || '',
      altText: pageContext.blogPostData.mainImage?.altText,
    },
    createdAt: pageContext.blogPostData.date?.split('T')[0] || '',
    category: {
      useCase: pageContext.blogPostData.blogCategory?.useCase || '',
      title: pageContext.blogPostData.blogCategory?.title || '',
    },
    author: pageContext.blogPostData.author || {},
    tags: pageContext.blogPostData.blogTags || [],
    generics: pageContext.blogPostData.microcopy?.values || [],
    featuredResources: pageContext.blogPostData.blogFeaturedResources || [],
  }

  let blogCategoryFormatted = blogData.category.title
    ? blogData.category.title
        .toLowerCase()
        .replace(/ *\([^)]*\) */g, '')
        .replace(/\s/g, '-')
    : ''

  if (blogCategoryFormatted === 'robotic-process-automation') {
    blogCategoryFormatted = 'rpa'
  } else if (blogCategoryFormatted === 'artificial-intelligence') {
    blogCategoryFormatted = 'ai'
  }

  let sameArticle = blogData.relatedArticles.findIndex(
    (post: any) =>
      post.h1Title && blogData.name && post.h1Title.toLowerCase() === blogData.name.toLowerCase()
  )

  if (sameArticle !== -1 && sameArticle !== 3) {
    blogData.relatedArticles = blogData.relatedArticles.filter(
      (post: any) => post.h1Title.toLowerCase() !== blogData.name.toLowerCase()
    )
  } else {
    blogData.relatedArticles = blogData.relatedArticles.slice(0, 3)
  }

  return (
    <Layout.Template
      lang={blogData.node_locale}
      pageType="blog"
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}>
      <div data-cy="BlogPost">
        {pageContext && pageContext.blogNav && (
          <BlogNavigation
            blogNav={pageContext.blogNav}
            pagePath={blogData.blogPostPath}
            useCase={blogData.category.useCase}
          />
        )}

        <SEOTags
          seoMetadata={blogData.seo}
          path={blogData.blogPostPath}
          slug={blogData.slug}
          locale={blogData.node_locale}
        />

        <BlogPostContainer $isSecondaryBlogPost={blogData.isSecondaryBlog}>
          <div className="BlogPost-Bg-Container"></div>
          <Container>
            <TopSection blogData={blogData} />
            <PostContent blogData={blogData} blogCategoryFormatted={blogCategoryFormatted} />
          </Container>

          <div className="BlogPost__Related-Articles">
            <RelatedArticles data={blogData.relatedArticles} locale={blogData.node_locale} />
          </div>

          {blogData.blogType !== UseCaseTypes.HR_BLOG && (
            <>
              <Subscribe data={pageContext.subscribe} />
              <SubscribeModal data={pageContext.subscribe} />
            </>
          )}
        </BlogPostContainer>
      </div>
    </Layout.Template>
  )
}

export default BlogPost
